import React, { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import TeamBannerCropper from 'components/teams/TeamBannerCropper'
import api from 'api/api'
import isGifFile from 'utils/isGifFile'

const BANNER_MAX_BYTES = 10 * 1024 * 1024 // 10MB, same cap as stream banners

interface UseUserBannerUploadParams {
    user: { id: string | number } | null | undefined
    onUpdated?: (user: any) => void
}

// Shared profile banner upload flow: hidden file input -> 3:1 cropper -> PATCH
// /api/v1/users/:id with a `banner_image` FormData field (same route the avatar
// upload uses). Render `bannerUploadUi` somewhere in the tree and call
// `openBannerPicker()` from any edit affordance.
export default function useUserBannerUpload({ user, onUpdated }: UseUserBannerUploadParams) {
    const inputRef = useRef<HTMLInputElement>(null)
    const [selectedImage, setSelectedImage] = useState<string | null>(null)

    const openBannerPicker = () => {
        inputRef.current?.click()
    }

    const handleSelect = async (ev: React.ChangeEvent<HTMLInputElement>) => {
        const input = ev.target
        const file = input.files?.[0]
        // Reset the input so the same file can be selected again
        input.value = ''
        if (file && file.size > BANNER_MAX_BYTES) {
            alert('File too large. Please select an image under 10MB.')
            return
        }

        if (file && (await isGifFile(file))) {
            // The cropper redraws through a canvas, which keeps only the first
            // frame. Upload GIFs as-is so they stay animated; banners render with
            // object-fit: cover, and the server skips variants for GIFs.
            handleSave(new Blob([file], { type: 'image/gif' }), 'banner.gif')
        } else if (file) {
            const reader = new FileReader()
            reader.onload = e => {
                setSelectedImage(e.target?.result as string)
            }
            reader.readAsDataURL(file)
        }
    }

    const handleSave = async (bannerBlob: Blob, filename = 'banner.png') => {
        if (!user?.id) return

        const formData = new FormData()
        formData.append('banner_image', bannerBlob, filename)

        const data = await api.updateUser(user.id, formData)
        if (data?.user) {
            setSelectedImage(null)
            onUpdated?.(data.user)
        } else {
            alert(data?.errors?.join(' ') || 'Failed to update banner. Please try again.')
        }
    }

    const bannerUploadUi = (
        <>
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                onChange={handleSelect}
                style={{ display: 'none' }}
            />
            {selectedImage &&
                createPortal(
                    <TeamBannerCropper
                        image={selectedImage}
                        onSave={handleSave}
                        onCancel={() => setSelectedImage(null)}
                    />,
                    document.body,
                )}
        </>
    )

    return { openBannerPicker, bannerUploadUi }
}
